import wretch, { ConfiguredMiddleware } from 'wretch'
import AbortAddon from 'wretch/addons/abort'
import QueryStringAddon from 'wretch/addons/queryString'
import { dedupe } from 'wretch/middlewares'
import { WretchError } from 'wretch/resolver'

import { StatusCode } from '@wwt/api/base/base'

import { getServerUrl } from '@wwt/core/admin'
import { JWTToken } from '@wwt/entities/api/search-response'
import { resetAll } from '@wwt/shared/utils'

import * as Errors from '../lib/errors'
import { useSession } from '../lib/hooks/useSession'

const authMiddleware: ConfiguredMiddleware = next => (url, options) => {
	const token = useSession.getState().token

	if (token !== null) {
		options.headers = {
			...options.headers,
			Authorization: `Bearer ${token}`
		}
	}

	return next(url, options)
}

type ResponseType = 'json' | 'blob' | 'text' | 'res'

const deduped = wretch().middlewares([dedupe()])

export const api = (responseType: ResponseType = 'json') =>
	wretch(`${getServerUrl()}/api`)
		.addon(QueryStringAddon)
		.addon(AbortAddon())
		.options({ credentials: 'include' })
		.errorType('json')
		.middlewares([authMiddleware])
		.catcher(StatusCode.ClientErrorUnauthorized, async (_error, req) => {
			try {
				const data = await deduped
					.get(
						`${getServerUrl()}/api/v1/auth/refresh?token=${useSession.getState().token}`
					)
					.json()

				const { token } = JWTToken.parse(data)

				useSession.getState().signIn({ token })

				const res = req
					.auth(token)
					.fetch()
					.unauthorized(err => {
						throw err
					})

				switch (responseType) {
					case 'json':
						return res.json()
					case 'blob':
						return res.blob()
					case 'text':
						return res.text()
					case 'res':
						return res.res()
					default:
						return res.json()
				}
			} catch (err) {
				// if it's a 401 error, we need to sign out
				if (err instanceof WretchError) {
					if (err.status === StatusCode.ClientErrorUnauthorized) {
						resetAll()
						return
					}
				}
				throw err
			}
		})
		.catcher(StatusCode.ServerErrorInternal, error => {
			throw new Errors.ServerUnavailableError(
				StatusCode.ServerErrorInternal,
				error.message
			)
		})
		.catcher(StatusCode.ServerErrorBadGateway, error => {
			throw new Errors.ServerUnavailableError(
				StatusCode.ServerErrorBadGateway,
				error.message
			)
		})
		.catcher(StatusCode.ServerErrorServiceUnavailable, error => {
			throw new Errors.ServerUnavailableError(
				StatusCode.ServerErrorServiceUnavailable,
				error.message
			)
		})
